import logo from "@/assets/logo.png";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import {
  Calendar,
  ChevronDown,
  DollarSign,
  Menu,
  MessageSquare,
  X
} from "lucide-react";
import { useEffect, useState } from "react";

const Header = () => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Buscar páginas institucionais do banco de dados
  const { data: paginasInstitucionais = [] } = useQuery({
    queryKey: ['paginas-institucionais-nav'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('paginas_institucionais')
        .select('titulo, slug')
        .order('titulo', { ascending: true });

      if (error) {
        console.error('Erro ao carregar páginas institucionais:', error);
        return [];
      }
      return data;
    },
  });

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    {
      label: "Institucional",
      items: [
        {
          label: "Quem Somos",
          href: "/quem-somos",
        },
        {
          label: "Comitê Gestor",
          href: "/comite-gestor",
        },
        {
          label: "Transparência",
          href: "/transparencia",
          direct: true,
        },
        {
          label: "Compliance",
          href: "/compliance",
          direct: true,
        },
      ]
    },
    {
      label: "Programas",
      href: "/projetos",
      direct: true,
    },
    {
      label: "Notícias",
      href: "/noticias",
      direct: true,
    },
    {
      label: "CTECNO's",
      href: "/ctecnos",
      direct: true,
    },
    {
      label: "Publicações",
      items: [
        { label: "Boletim Técnico", href: "/publicacoes/boletim-tecnico" },
        { label: "Circular Tecnica", href: "/publicacoes/circular-tecnica" },
      ],
    },
    {
      label: "Mídias",
      href: "/comunicacao/midias",
      direct: true,
    },

    {
      label: "Contato",
      items: [
        {
          label: "Agende uma Visita",
          href: "/contato/agende-visita",
          icon: Calendar,
          description: "Conheça nossos CTECNOs",
        },
        {
          label: "Solicitar Apoio",
          href: "/solicitacao-apoio",
          icon: DollarSign,
          description: "Solicite apoio financeiro para projetos",
        },
        {
          label: "Ouvidoria",
          href: "/contato/ouvidoria",
          icon: MessageSquare,
          description: "Denúncias, reclamações e sugestões",
        },
      ],
    },
  ];

  return (
    <header
      className={`sticky top-0 z-50 py-1 bg-background transition-all duration-100 ${scrolled ? "shadow-md backdrop-blur-sm bg-background/80" : "shadow-sm border-b border-border"}`}
    >
      <div className="mx-4 md:mx-8 lg:mx-16 xl:mx-80">
        <div
          className={`flex items-center justify-between transition-all duration-200`}
        >
          {/* Logo */}
          <a href="/" className="flex items-center shrink-0 min-w-fit">
            <img
              src={logo}
              alt="IAGRO-MT"
              className={`transition-all duration-200 h-12 md:h-14 lg:h-16 w-auto object-contain`}
            />
          </a>

          {/* Desktop Navigation & Search */}
          <div className="hidden lg:flex items-center gap-4 xl:gap-6 shrink-0">
            <nav className="flex items-center space-x-3 lg:space-x-4 xl:space-x-6">
              {navItems.map((item) => (
                <div key={item.label} className="shrink-0">
                  {item.direct ? (
                    <a
                      href={item.href}
                      className="text-foreground hover:text-primary transition-colors font-semibold text-xs lg:text-sm xl:text-base whitespace-nowrap"
                    >
                      {item.label}
                    </a>
                  ) : (
                    <div className="relative group">
                      <button className="flex items-center gap-1 text-foreground hover:text-primary transition-colors font-semibold text-xs lg:text-sm xl:text-base whitespace-nowrap">
                        {item.label}
                        <ChevronDown className="h-3 w-3 lg:h-4 lg:w-4" />
                      </button>
                      <div className="absolute top-full left-0 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform translate-y-[-10px] group-hover:translate-y-0">
                        <div className="bg-popover border border-border rounded-lg shadow-lg py-2 min-w-[240px] z-50">
                          {item.items?.map((subitem) => {
                            const Icon = subitem.icon;
                            return (
                              <a
                                key={subitem.label}
                                href={subitem.href}
                                className={`block px-5 py-3 hover:bg-muted/50 transition-colors ${subitem.highlight ? "bg-secondary/10" : ""}`}
                              >
                                <div className="flex items-start gap-3">
                                  {Icon && <Icon className="h-5 w-5 text-primary mt-0.5 shrink-0" />}
                                  <div>
                                    <div className="font-medium text-foreground text-[15px]">{subitem.label}</div>
                                    {subitem.description && (
                                      <div className="text-[13px] text-muted-foreground mt-0.5">
                                        {subitem.description}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </nav>

            {/* Search & Social Links */}
            <div className="flex items-center gap-4 shrink-0">
              <div className="relative">
                {isSearchOpen ? (
                  <div className="flex items-center">
                    <Input type="search" placeholder="Buscar..." className="w-64" autoFocus />
                    <Button variant="ghost" size="icon" onClick={() => setIsSearchOpen(false)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <>
                    {/* <Button variant="ghost" size="icon" onClick={() => setIsSearchOpen(true)}>
                   <Search className="h-5 w-5" />
                 </Button> */}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Mobile/Tablet Menu */}
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden shrink-0">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="overflow-y-auto rounded-md">
              {/* Search */}
              {/* <div className="mb-6">
                <Input type="search" placeholder="Buscar..." className="w-full" />
              </div> */}

              {/* Navigation */}
              <Accordion type="single" collapsible className="w-full">
                {navItems.map((item, index) => (
                  <div key={item.label}>
                    {item.direct ? (
                      <a
                        href={item.href}
                        className="block py-4 text-foreground hover:text-primary font-semibold border-b border-border"
                      >
                        {item.label}
                      </a>
                    ) : (
                      <AccordionItem value={`item-${index}`}>
                        <AccordionTrigger className="text-foreground font-semibold">{item.label}</AccordionTrigger>
                        <AccordionContent>
                          <div className="space-y-2 pl-4">
                            {item.items?.map((subitem) => {
                              const Icon = subitem.icon;
                              return (
                                <a key={subitem.label} href={subitem.href} className="block py-2">
                                  <div className="flex items-start gap-2">
                                    {Icon && <Icon className="h-4 w-4 text-primary mt-0.5 shrink-0" />}
                                    <div>
                                      <div className="text-sm font-medium text-foreground">{subitem.label}</div>
                                      {subitem.description && (
                                        <div className="text-xs text-muted-foreground mt-0.5">
                                          {subitem.description}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </a>
                              );
                            })}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    )}
                  </div>
                ))}
              </Accordion>

              {/* Social Links */}
              {/* <div className="mt-8 pt-6 border-t border-border">
                <div className="text-sm font-semibold text-foreground mb-4">Redes Sociais</div>
                <div className="flex gap-4">
                  <a
                    href="https://facebook.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-primary transition-colors"
                  >
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                  </a>
                  <a
                    href="https://instagram.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-primary transition-colors"
                  >
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                    </svg>
                  </a>
                  <a
                    href="https://linkedin.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-primary transition-colors"
                  >
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                  </a>
                  <a
                    href="https://youtube.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-primary transition-colors"
                  >
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                    </svg>
                  </a>
                </div>
              </div> */}
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
